/**
 * A separate script for alpine.js store
 * because Alpine.js should only be loaded after this script is loaded
 * and we don't want to block main.js from loading because we are waiting for alpine.js to load
 */
document.addEventListener("alpine:init", () => {
  document.querySelectorAll(".section").forEach((el) => {
    el.setAttribute("x-data", `{ expanded: false }`);
    el.setAttribute("x-on:click", "expanded = !expanded");
    el.setAttribute("x-intersect:enter", `$el.classList.add('fadeInUp')`);
  });

  document.querySelectorAll(".expandable-sections .expandable-content").forEach((el, idx) => {
    el.setAttribute("x-data", "{ height: 0 }");
    el.setAttribute("x-init", "height = $el.scrollHeight");
    el.setAttribute(":style", "{ 'max-height': expanded ? `${height}px` : '0px' }");
    el.setAttribute(":class", "{ 'open': expanded }");
  });

  Alpine.store("slideshowFullscreen", false);

  Alpine.store("footerLogoVisible", false);
  Alpine.store("headerTextColor", "text-body"); // "body" or "white"

  Alpine.data("postGrid", () => {
    return {
      checkedTerms: [],
      isVisible(termIds) {
        if (this.checkedTerms.length === 0) return true;
        const itemTermIds = termIds.split(",").map((id) => parseInt(id));
        return itemTermIds.some((termId) => this.checkedTerms.includes(termId));
      },
    };
  });

  Alpine.data("introScreen", ({ hours = 12 } = {}) => ({
    introScreenVisible: false,
    hours,

    init() {
      const lastSeen = localStorage.getItem("introSeenAt");
      const now = Date.now();
      const expiry = this.hours * 60 * 60 * 1000; // hours → ms

      // Show if never seen OR expired
      if (!lastSeen || now - Number(lastSeen) > expiry) {
        this.introScreenVisible = true;
      }

      // Catch clicks on .intro-exit inside Gutenberg content
      this.$el.addEventListener("click", (e) => {
        if (e.target.closest(".intro-exit")) {
          this.close();
        }
      });

      // Auto close intro screen after 5 seconds
      setTimeout(() => {
        if (this.introScreenVisible) {
          this.close();
        }
      }, 9000);
    },

    close() {
      this.introScreenVisible = false;
      localStorage.setItem("introSeenAt", Date.now().toString());
    },
  }));

  Alpine.data("headerColorDetector", () => ({
    init() {
      // Delay initial detection to ensure Alpine is fully loaded
      setTimeout(() => {
        this.detectBackgroundColor();
      }, 100);

      // Throttle the scroll event for better performance
      let scrollTimer;
      window.addEventListener("scroll", () => {
        if (scrollTimer) clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.detectBackgroundColor(), 50);
      });
      window.addEventListener("resize", () => this.detectBackgroundColor());
    },

    detectBackgroundColor() {
      const header = document.querySelector("header");
      if (!header) return;

      if (document.querySelector(".visible-intro-screen")) return;

      const headerRect = header.getBoundingClientRect();
      const centerX = window.innerWidth / 2;
      const centerY = headerRect.top + headerRect.height / 2;

      // Get the element behind the header center
      // Temporarily hide the entire header to see what's behind it
      header.style.visibility = "hidden";
      const elementBehind = document.elementFromPoint(centerX, centerY);
      header.style.visibility = "visible";

      if (!elementBehind) return;

      const computedStyle = window.getComputedStyle(elementBehind);
      const backgroundColor = computedStyle.backgroundColor;
      const backgroundImage = computedStyle.backgroundImage;

      let isLightBackground = true;

      // Check background color
      if (backgroundColor && backgroundColor !== "rgba(0, 0, 0, 0)" && backgroundColor !== "transparent") {
        isLightBackground = this.isColorLight(backgroundColor);
      }
      // If there's a background image, assume it's dark (most common case)
      // You can customize this logic based on your specific images if needed
      else if (backgroundImage && backgroundImage !== "none") {
        isLightBackground = false;
      }
      // Check parent elements for background (including hero sections)
      else {
        let parent = elementBehind.parentElement;
        let searchDepth = 0;
        const maxDepth = 10; // Search deeper for hero images

        while (parent && parent !== document.body && searchDepth < maxDepth) {
          const parentStyle = window.getComputedStyle(parent);
          const parentBgColor = parentStyle.backgroundColor;
          const parentBgImage = parentStyle.backgroundImage;

          if (parentBgColor && parentBgColor !== "rgba(0, 0, 0, 0)" && parentBgColor !== "transparent") {
            isLightBackground = this.isColorLight(parentBgColor);
            break;
          } else if (parentBgImage && parentBgImage !== "none") {
            isLightBackground = false; // Assume dark for background images
            break;
          }
          parent = parent.parentElement;
          searchDepth++;
        }

        // If no background found in parents, check if we're over a hero section
        if (searchDepth <= maxDepth || !parent) {
          // Look for common hero section selectors
          const heroElements = document.querySelectorAll('.page-hero, [class*="hero"], .banner, [class*="banner"], .big-slideshow');
          for (const hero of heroElements) {
            const heroRect = hero.getBoundingClientRect();

            if (centerY >= heroRect.top && centerY <= heroRect.bottom) {
              const heroStyle = window.getComputedStyle(hero);
              if (heroStyle.backgroundImage && heroStyle.backgroundImage !== "none") {
                isLightBackground = false;
                break;
              }
            }
          }
        }
      }

      // Update store
      const newColor = isLightBackground ? "text-body" : "text-white";
      this.$store.headerTextColor = newColor;
    },

    isColorLight(color) {
      // Handle different color formats
      let r, g, b;

      if (color.startsWith("rgb(")) {
        const matches = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (matches) {
          r = parseInt(matches[1]);
          g = parseInt(matches[2]);
          b = parseInt(matches[3]);
        }
      } else if (color.startsWith("rgba(")) {
        const matches = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/);
        if (matches) {
          r = parseInt(matches[1]);
          g = parseInt(matches[2]);
          b = parseInt(matches[3]);
        }
      } else if (color.startsWith("#")) {
        const hex = color.slice(1);
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
      } else {
        // For named colors or other formats, default to light
        return true;
      }

      // Calculate relative luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    },
  }));
});
